import * as React from 'react';
import { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../authConfig';
import {
  addOrUpdateCourseDate,
  addOrUpdateCourseDay,
  deleteCourseDate,
  getCourseDates,
  getCourseDays,
} from '../api';
import {
  Checkbox,
  CommandBar,
  DatePicker,
  DayOfWeek,
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  Dropdown,
  IDropdownOption,
  ICommandBarItemProps,
  Label,
  Panel,
  PanelType,
  PrimaryButton,
  defaultDatePickerStrings,
} from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import moment from 'moment';
import { AxiosResponse } from 'axios';
import CourseDetailList from './CourseDetailList';
import type { ICourse, ICourseDay, ICourseType } from './DataTable';

interface DatePanelProps {
  courseType: ICourseType;
  onDismiss: () => void;
}

export default function DatePanel(props: DatePanelProps): JSX.Element {
  const { instance, accounts } = useMsal();
  const [courseDates, setCourseDates] = useState<ICourse[]>([]);
  const [courseDays, setCourseDays] = useState<ICourseDay[]>([]);
  const [courseGroup, setCourseGroup] = useState<{ [key: string]: ICourse[] }>({});
  const [token, setToken] = useState<string>('');

  const courseDurationOffset = Math.max(props.courseType.varighed - 1, 0);

  const [selectedCourseDate, setSelectedCourseDate] = useState<ICourseDay | null>(null);
  const [startDato, setStartDato] = useState<Date>(moment().startOf('day').toDate());
  const [slutDato, setSlutDato] = useState<Date>(() =>
    moment().startOf('day').add(courseDurationOffset, 'days').toDate()
  );

  const [hideDeleteDialog, setHideDeleteDialog] = useState<boolean>(true);
  const [hideDateDialog, setHideDateDialog] = useState<boolean>(true);
  const [isModalOpen, { setTrue: showModal, setFalse: hideModal }] = useBoolean(false);
  const [checked, setChecked] = useState<boolean>(false);
  const [courseId, setCourseId] = useState<number>(0);

  const _items: ICommandBarItemProps[] = [
    {
      key: 'newItem',
      onClick: () => {
        setSelectedCourseDate(null);
        toggleHideDateDialog();
      },
      text: 'Opret dato',
      iconProps: { iconName: 'Add' },
    },
  ];

  if (selectedCourseDate) {
    _items.push(
      ...[
        {
          key: 'editItem',
          text: 'Ret dato',
          iconProps: { iconName: 'Edit' },
          onClick: () => {
            if (selectedCourseDate.startDato) {
              setStartDato(moment(selectedCourseDate.startDato).startOf('day').toDate());
            }

            if (selectedCourseDate.slutDato) {
              setSlutDato(moment(selectedCourseDate.slutDato).startOf('day').toDate());
            }

            if (selectedCourseDate.kursusID) {
              setCourseId(selectedCourseDate.kursusID);
            }

            toggleHideDateDialog();
          },
        },
        {
          key: 'deleteItem',
          text: 'Slet dato',
          iconProps: { iconName: 'Delete' },
          onClick: () => {
            toggleHideDeleteDialog();
          },
        },
      ]
    );
  }

  useEffect(() => {
    instance
      .acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      })
      .then((response) => {
        setToken(response.accessToken);
      });
  }, [props]);

  useEffect(() => {
    fetchCourseDates();
  }, [token]);

  const fetchCourseDates = async (): Promise<void> => {
    try {
      const data = await getCourseDates(token, props.courseType.kursustypeID);
      if (!data || !data.data || !Array.isArray(data.data)) {
        console.error('Error', data);
        setCourseDates([]);
        return;
      }

      const courseDayResponses = await Promise.all(
        data.data.map(async (course: ICourse) => {
          try {
            const days = await getCourseDays(token, course.kursusID);
            if (days && days.data) {
              return days?.data || [];
            }
          } catch (error) {
            console.error(`Error fetching days for course ID ${course.kursusID}:`, error);
            return [];
          }
        })
      );

      const allDates = courseDayResponses.flat();
      setCourseDates(allDates);
    } catch (error) {
      console.error('Error fetching course dates:', error);
      setCourseDates([]);
    }
  };

  const renderDateDialog = () => {
    const dateDialogProps = {
      type: DialogType.normal,
      title: selectedCourseDate ? 'Ret dato' : 'Opret dato',
      closeButtonAriaLabel: 'Close',
    };

    const dropdownOptions: IDropdownOption[] = [];
    courseDates.forEach((courseDate) => {
      if (!dropdownOptions.find((option) => option.key === courseDate.kursusID)) {
        dropdownOptions.push({
          key: courseDate.kursusID,
          text: courseDate.kursusID.toString(),
        });
      }
    });

    return (
      <Dialog hidden={hideDateDialog} onDismiss={toggleHideDateDialog} dialogContentProps={dateDialogProps}>
        {!selectedCourseDate && (
          <Checkbox
            label="Skal datoen tilføjes til et eksisterende kursus?"
            onChange={(_, checked: boolean | undefined) => setChecked(checked ?? false)}
            checked={checked}
          />
        )}
        {checked && !selectedCourseDate && (
          <Dropdown
            label="Kursus ID"
            options={dropdownOptions}
            onChange={(_, option: IDropdownOption | undefined) => setCourseId(Number(option?.key))}
            selectedKey={courseId}
            placeholder="Vælg KursusID"
          />
        )}
        <br />
        <DatePicker
          firstDayOfWeek={DayOfWeek.Monday}
          value={moment(startDato).startOf('day').toDate()}
          formatDate={() => moment(startDato).format('DD-MM-YYYY')}
          onSelectDate={handleStartDateChange}
          placeholder="Vælg startdato..."
          strings={defaultDatePickerStrings}
        />
        {!selectedCourseDate && (
          <DatePicker
            firstDayOfWeek={DayOfWeek.Monday}
            value={moment(slutDato).startOf('day').toDate()}
            formatDate={() => moment(slutDato).format('DD-MM-YYYY')}
            onSelectDate={(date) => {
              if (date) {
                setSlutDato(moment(date).toDate());
              }
            }}
            placeholder="Vælg slutdato..."
            strings={defaultDatePickerStrings}
          />
        )}

        <DialogFooter>
          <PrimaryButton
            onClick={() => {
              onAddOrUpdate().then(() => {
                toggleHideDateDialog();
                setChecked(false);
                setCourseId(0);
              });
            }}
            text={selectedCourseDate ? 'Ret' : 'Opret'}
          />
          <DefaultButton
            onClick={() => {
              setStartDato(moment().startOf('day').toDate());
              setSlutDato(moment().add(courseDurationOffset, 'days').startOf('day').toDate());
              setCourseId(0);
              setChecked(false);
              toggleHideDateDialog();
            }}
            text="Fortryd"
          />
        </DialogFooter>
      </Dialog>
    );
  };

  const handleStartDateChange = (date: Date | null | undefined): void => {
    if (!date) {
      return;
    }

    const normalizedStart = moment(date).startOf('day');
    setStartDato(normalizedStart.toDate());

    if (selectedCourseDate) {
      setSlutDato(normalizedStart.toDate());
      return;
    }

    const calculatedEnd = normalizedStart.clone().add(courseDurationOffset, 'days');
    setSlutDato(calculatedEnd.toDate());
  };

  const renderDeleteDialog = () => {
    const deleteDialogProps = {
      type: DialogType.normal,
      title: 'Slet dato',
      closeButtonAriaLabel: 'Close',
      subText: 'Er du sikker på at du vil slette datoen?',
    };

    return (
      <Dialog
        hidden={hideDeleteDialog}
        onDismiss={toggleHideDeleteDialog}
        dialogContentProps={deleteDialogProps}
      >
        <DialogFooter>
          <PrimaryButton
            onClick={() => {
              onDateDelete().then(() => {
                toggleHideDeleteDialog();
              });
            }}
            text="Slet"
          />
          <DefaultButton onClick={toggleHideDeleteDialog} text="Fortryd" />
        </DialogFooter>
      </Dialog>
    );
  };

  const toggleHideDeleteDialog = (): void => {
    setHideDeleteDialog(!hideDeleteDialog);
  };

  const toggleHideDateDialog = (): void => {
    setHideDateDialog(!hideDateDialog);
  };

  const onDateDelete = async (): Promise<void> => {
    try {
      if (selectedCourseDate) {
        await deleteCourseDate(token, selectedCourseDate.kursusdagID);
      }
    } catch (error) {
      console.log(error);
    } finally {
      await fetchCourseDates();
    }
  };

  const onAddOrUpdate = async (): Promise<void> => {
    let tempId = 1;
    let counter = 0;

    const normalizedStart = moment(startDato).startOf('day').toDate();
    const normalizedEnd = moment(slutDato).startOf('day').toDate();

    const incrementDay = (date: Date): Date => {
      const next = new Date(date);
      next.setDate(next.getDate() + 1);
      return next;
    };

    for (let currentDate = new Date(normalizedStart); currentDate <= normalizedEnd; currentDate = incrementDay(currentDate)) {
      try {
        const newObj: ICourse = {
          kursusID: 0,
          startDato: normalizedStart,
          slutDato: normalizedEnd,
          kursustypeID: props.courseType.kursustypeID,
          antalDeltagere: 0,
          kursusAdresse: 5216,
          kursusLokale: 0,
          kursusTitel: props.courseType.kursusTitel,
          kursustype: 'Åbent kursus',
          startDato1Tid: '09001600',
          startDatoTid: '09001600',
        };

        const newDayObj: ICourseDay = {
          kursusdagID: selectedCourseDate?.kursusdagID ?? 0,
          kursusID: courseId == 0 ? tempId : courseId,
          startDato: currentDate,
          slutDato: currentDate,
        };

        if (selectedCourseDate && counter <= 0) {
          selectedCourseDate.startDato = currentDate;
          selectedCourseDate.slutDato = currentDate;
        }

        if (newDayObj.kursusID === 1 && !selectedCourseDate) {
          const newCourse: AxiosResponse<ICourse> = await addOrUpdateCourseDate(token, newObj);
          newDayObj.kursusID = newCourse.data.kursusID;
          tempId = newCourse.data.kursusID;
        }

        await addOrUpdateCourseDay(token, newDayObj);
        counter++;
      } catch (error) {
        console.log(error);
      } finally {
        await fetchCourseDates();
      }
    }
  };

  useEffect(() => {
    const tempCourseGroup: { [key: string]: ICourse[] } = {};
    courseDates.forEach((item) => {
      if (!(item.kursusID in tempCourseGroup)) {
        tempCourseGroup[item.kursusID] = [item];
      } else {
        tempCourseGroup[item.kursusID].push(item);
      }
    });

    setCourseGroup(tempCourseGroup);
  }, [courseDates]);

  return (
    <Panel
      headerText={props?.courseType.kursusTitel}
      type={PanelType.medium}
      isOpen={props !== null}
      onDismiss={() => props.onDismiss()}
      closeButtonAriaLabel="Close"
    >
      <>
      </>
      <p>Administrer datoer</p>
      <div style={{ height: 50, marginLeft: '-24px' }}>
        <CommandBar
          items={_items}
          ariaLabel="Inbox actions"
          primaryGroupAriaLabel="Email actions"
          farItemsGroupAriaLabel="More actions"
        />
      </div>

      {Object.keys(courseGroup).map((key) => {
        const dateColumns = [
          {
            name: 'Start dato',
            key: 'startDato',
            maxWidth: 100,
            minWidth: 50,
            fieldName: 'startDato',
            onRender: (item: any) => {
              return (
                <span>
                  {new Date(item.startDato)
                    .toLocaleDateString('da-DK', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                    })
                    .replaceAll('.', '-')}
                </span>
              );
            },
          },
          {
            name: 'Slut dato',
            key: 'slutDato',
            fieldName: 'slutDato',
            maxWidth: 250,
            minWidth: 50,
            onRender: (item: any) => {
              const date = item.slutDato;
              return (
                <span>
                  {new Date(date)
                    .toLocaleDateString('da-DK', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                    })
                    .replaceAll('.', '-')}
                </span>
              );
            },
          },
        ];

        return (
          <div style={{ margin: 20 }} key={key}>
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <Label style={{ marginRight: '1.5rem' }}>{key}</Label>
            </div>

            <div>
              <CourseDetailList
                items={courseGroup[key]}
                setSelectedCourseDate={(item) => setSelectedCourseDate(item)}
              />
            </div>
          </div>
        );
      })}

      {renderDateDialog()}
      {renderDeleteDialog()}
    </Panel>
  );
}
